import type { BoxProps } from '@iheartjane/reefer';
import { Box, Flex, Image, SparkleIcon, Typography } from '@iheartjane/reefer';
import type { SearchPosition } from '@iheartjane/types';

export const BUTTON_HEIGHT = 48;
export const TEXT_HEIGHT = 24;

export const PreviewSkeleton = ({
  background = 'grays-light',
  borderRadius = 'lg',
  ...props
}: BoxProps) => {
  return <Box background={background} borderRadius={borderRadius} {...props} />;
};

export const AiButtonSkeleton = ({
  height = BUTTON_HEIGHT,
}: {
  height?: number;
}) => (
  <Flex
    alignItems="center"
    background="primary"
    borderRadius="lg"
    data-testid="ai-button"
    gap={8}
    px={16}
    height={height}
    width={168}
  >
    <SparkleIcon color="grays-white" size="sm" />
    <PreviewSkeleton background="grays-ultralight" height={18} width={112} />
  </Flex>
);

const HeaderLogo = ({
  isMobile,
  logoUrl,
}: {
  isMobile: boolean;
  logoUrl?: string;
}) =>
  logoUrl ? (
    <Image
      data-testid="logo-image"
      src={logoUrl}
      altText="Menu logo"
      height={`${BUTTON_HEIGHT}px`}
      width="auto"
      objectFit="contain"
    />
  ) : (
    <Flex
      data-testid="logo"
      alignItems="center"
      background="grays-ultralight"
      border="grays-light"
      borderRadius="sm"
      justifyContent="center"
      height={BUTTON_HEIGHT}
      minWidth={isMobile ? 126 : 152}
      style={{
        borderStyle: 'dashed',
      }}
    >
      <Typography color="grays-mid" variant="caps">
        Logo
      </Typography>
    </Flex>
  );

export const HeaderSkeleton = ({
  isMobile = false,
  logoUrl = '',
  searchPosition = 'split',
  whiteHeader = false,
}: {
  isMobile?: boolean;
  logoUrl?: string;
  searchPosition: SearchPosition;
  whiteHeader?: boolean;
}) => {
  const searchInHeader = ['split', 'header'].includes(searchPosition);

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Flex gap={24}>
          {(isMobile || searchInHeader) && (
            <HeaderLogo isMobile={isMobile} logoUrl={logoUrl} />
          )}

          {/* Store switcher - desktop */}
          {!isMobile && (
            <PreviewSkeleton
              data-testid="store-switcher-desktop"
              background={
                whiteHeader ? 'transparent-black-twenty' : 'grays-light'
              }
              borderRadius="sm"
              height={BUTTON_HEIGHT}
              width={250}
              mr={12}
            />
          )}
        </Flex>

        {!isMobile && !searchInHeader && (
          <HeaderLogo isMobile={isMobile} logoUrl={logoUrl} />
        )}

        <Flex alignItems="center" gap={isMobile ? 12 : 24}>
          {!isMobile && searchInHeader && (
            <>
              <SearchSkeleton />

              {searchPosition === 'header' && <AiButtonSkeleton />}
            </>
          )}

          {Array.from({
            length: !searchInHeader || isMobile ? 4 : 3,
          }).map((_, i) => (
            <PreviewSkeleton
              key={i}
              height={32}
              width={32}
              background={
                whiteHeader ? 'transparent-black-twenty' : 'grays-light'
              }
            />
          ))}
        </Flex>
      </Flex>

      {/* Store switcher - mobile */}
      {isMobile && (
        <Box data-testid="store-switcher-mobile" width="100%">
          <PreviewSkeleton
            background={
              whiteHeader ? 'transparent-black-twenty' : 'grays-light'
            }
            borderRadius="sm"
            height={BUTTON_HEIGHT}
            width="100%"
          />
        </Box>
      )}
    </>
  );
};

export const SearchSkeleton = () => (
  <Flex
    alignItems="center"
    background="grays-white"
    border="grays-light"
    borderRadius="xl"
    gap={12}
    px={16}
    py={8}
    height={BUTTON_HEIGHT}
    width={240}
  >
    <PreviewSkeleton height={32} width={32} />
    <PreviewSkeleton height={24} width={100} />
  </Flex>
);

export const FilterBarSkeleton = ({
  searchPosition,
  isMobile = false,
}: {
  isMobile?: boolean;
  searchPosition: SearchPosition;
}) => {
  return (
    <>
      <Flex gap={isMobile ? 12 : 16} overflow="hidden" mr={isMobile ? 0 : 16}>
        {searchPosition === 'filters' ? (
          <>
            <SearchSkeleton />

            {isMobile ? (
              <>
                <Flex
                  alignItems="center"
                  background="primary"
                  borderRadius="lg"
                  data-testid="ai-button"
                  px={12}
                  height={BUTTON_HEIGHT}
                  width={BUTTON_HEIGHT}
                >
                  <SparkleIcon color="grays-white" />
                </Flex>
                <PreviewSkeleton height={BUTTON_HEIGHT} width={BUTTON_HEIGHT} />
              </>
            ) : (
              <AiButtonSkeleton />
            )}

            {!isMobile && (
              <Flex gap={16}>
                <PreviewSkeleton height={BUTTON_HEIGHT} width={156} />
                <PreviewSkeleton height={BUTTON_HEIGHT} width={120} />
              </Flex>
            )}
          </>
        ) : (
          <>
            <PreviewSkeleton
              height={BUTTON_HEIGHT}
              width={isMobile ? 120 : BUTTON_HEIGHT}
            />
            {searchPosition === 'split' && <AiButtonSkeleton />}

            {!isMobile && (
              <Flex gap={16}>
                <PreviewSkeleton height={BUTTON_HEIGHT} width={156} />

                {Array.from({
                  length: 4,
                }).map((_, i) => (
                  <PreviewSkeleton key={i} height={BUTTON_HEIGHT} width={120} />
                ))}
              </Flex>
            )}
          </>
        )}
      </Flex>

      {!isMobile && <PreviewSkeleton height={BUTTON_HEIGHT} width={156} />}
    </>
  );
};

export const FooterSkeleton = ({
  isMobile = false,
}: {
  isMobile?: boolean;
}) => {
  const TITLE_TEXT_HEIGHT = isMobile ? TEXT_HEIGHT : 32;

  return (
    <>
      <Flex
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        gap={24}
        mb={40}
      >
        <Flex gap={16} flexDirection="column">
          <PreviewSkeleton height={TITLE_TEXT_HEIGHT} width={156} />
          <PreviewSkeleton height={TEXT_HEIGHT} width={198} />
          <PreviewSkeleton height={BUTTON_HEIGHT} width={156} />
        </Flex>

        <Flex flexDirection={isMobile ? 'column' : 'row'} gap={32}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Flex key={i} gap={16} flexDirection="column">
              <PreviewSkeleton
                height={TITLE_TEXT_HEIGHT}
                width={120}
                mt={isMobile ? 40 : 0}
                mb={16}
              />
              <PreviewSkeleton height={TEXT_HEIGHT} width={88} />
              <PreviewSkeleton height={TEXT_HEIGHT} width={88} />
              <PreviewSkeleton height={TEXT_HEIGHT} width={88} />
            </Flex>
          ))}
        </Flex>
      </Flex>

      <Box
        background="grays-light"
        borderRadius="lg"
        height={TITLE_TEXT_HEIGHT}
        width={107}
        mb={24}
      />
      <Flex gap={16}>
        {Array.from({ length: isMobile ? 3 : 6 }).map((_, i) => (
          <PreviewSkeleton key={i} height={TEXT_HEIGHT} width={88} />
        ))}
      </Flex>
    </>
  );
};
